import AppContext from "PFApp/app_context";
import SimpleBadge from "PFComponents/badges_list/simple_badge";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import Tooltip from "PFComponents/tooltip/tooltip";
import { Typography } from "PFComponents/typography";
import { IconName } from "PFTheme/graphics/icons";
import { PureCustomValue } from "PFTypes";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "../skills_section.module.scss";

type MarketplaceSkillBadgeProps = {
  skill: PureCustomValue & { top?: boolean; developmental?: boolean };
  kind: "matching" | "unmatched";
};

export const MarketplaceSkillBadge = ({ skill, kind }: MarketplaceSkillBadgeProps): JSX.Element => {
  const color = useMemo(
    () =>
      ({
        matching: "paletteGreen1",
        unmatched: "paletteRed1"
      }[kind]),
    [kind]
  );

  const iconName = useMemo(
    () =>
      ({
        matching: "check",
        unmatched: "cross"
      }[kind] as IconName),
    [kind]
  );

  const Wrapper = useMemo(
    () =>
      kind === "unmatched"
        ? ({ children }) => (
            <Tooltip content={<UnmatchedSkillTooltipContent skillName={skill.text} />} interactive>
              <div>{children}</div>
            </Tooltip>
          )
        : React.Fragment,
    [kind, skill.text]
  );

  return (
    <Wrapper>
      <SimpleBadge
        key={skill.id}
        style={{ color: `var(--${color})` }}
        text={skill.text}
        classes={{ root: css.skillBadge }}
        icon={
          <div className={css.icon}>
            <Icon name={iconName} size="sm" fill={color} />
          </div>
        }
      />
    </Wrapper>
  );
};

const SKILL_PLACEHOLDER = "<TERM>";

type UnmatchedSkillTooltipContentProps = {
  skillName: string;
};

const UnmatchedSkillTooltipContent = ({ skillName }: UnmatchedSkillTooltipContentProps): JSX.Element => {
  const { t } = useTranslation("marketplace", { keyPrefix: "workOpportunities.roleDetails.skills" });
  const {
    store: {
      currentAccount: { customization }
    }
  } = useContext(AppContext);
  const learningPlatformLinkPlaceholder = customization.learning_platform_link;
  const isLearningPlatformLinkValid =
    !!learningPlatformLinkPlaceholder && learningPlatformLinkPlaceholder.includes(SKILL_PLACEHOLDER);
  const learningPlatformLink = learningPlatformLinkPlaceholder?.replace(SKILL_PLACEHOLDER, skillName);

  return (
    <div className={css.skillTooltip}>
      <Typography variant="bodyRegular" noMargin>
        {t("missingSkill")}
      </Typography>
      {isLearningPlatformLinkValid && (
        <Button kind="text" tag="a" href={learningPlatformLink} target="_blank">
          <span className={css.optionToDevelop}>
            <Icon name="development" size="sm" />
            <Typography variant="bodyButtons" noMargin>
              {t("optionToDevelopMissingSkill")}
            </Typography>
          </span>
        </Button>
      )}
    </div>
  );
};
